import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isProduction } from 'config';
import dayjs from 'dayjs';
import type { DiscountType } from 'types/subscriptions.types';
import { AvailableCountries } from 'types/users.types';
import { isFreeOrTrialWithoutPreviousSubscription } from 'utils/subscriptions.utils';
import formatMoney from 'utils/formatMoney';
import { isNewUserRelativeToDate } from 'hooks/useIsNewUserRelativeToDate';
import globalModalsController from 'components/organisms/GlobalModalsService/globalModalsController';
import StarterDiscountForm from 'components/organisms/StarterDiscountForm';

type DiscountsType = {
  monthly?: DiscountType;
  yearly?: DiscountType;
};

const GERMANY_FIRST_SUBSCRIPTION_MONTHLY_85_DISCOUNT_PROD = 'jfqFRTv4';

const GERMANY_FIRST_SUBSCRIPTION_YEARLY_21_DISCOUNT_PROD = '7w49sJnU';

const GERMANY_FIRST_SUBSCRIPTION_MONTHLY_85_DISCOUNT_STAGING = 'f9tBzgsp';

const GERMANY_FIRST_SUBSCRIPTION_YEARLY_21_DISCOUNT_STAGING = 'g90BOo99';

const BELGIUM_STARTER_DISCOUNT_PROD = 'gtGzoZwb';

const BELGIUM_STARTER_DISCOUNT_STAGING = 'tfFUrK7a';

const yearlyPromocode = isProduction
  ? GERMANY_FIRST_SUBSCRIPTION_YEARLY_21_DISCOUNT_PROD
  : GERMANY_FIRST_SUBSCRIPTION_YEARLY_21_DISCOUNT_STAGING;

const monthlyPromocode = isProduction
  ? GERMANY_FIRST_SUBSCRIPTION_MONTHLY_85_DISCOUNT_PROD
  : GERMANY_FIRST_SUBSCRIPTION_MONTHLY_85_DISCOUNT_STAGING;

const starterDiscountPromocode = isProduction
  ? BELGIUM_STARTER_DISCOUNT_PROD
  : BELGIUM_STARTER_DISCOUNT_STAGING;

const useDiscount = (): DiscountsType | undefined => {
  const { t } = useTranslation();

  const { data: user } = useGetCurrentCustomerData();

  const userSubscription = user?.subscriptionPlan;

  const isNewYearUser = isNewUserRelativeToDate(
    user?.created as string,
    dayjs('2026-01-01').toISOString(),
  );

  const startedLessThanAYearAgo =
    user?.VATNumber && dayjs().diff(dayjs(user?.startDate), 'day') < 365;

  const monthlyFirstUseDiscount: DiscountType = useMemo(
    () => ({
      couponId: monthlyPromocode,
      discountValue: 0.85,
      discountType: 'percentage',
      isActive: true,
      discountText: t('discounts.new_users_discount_text', {
        percent: 85,
      }),
      planWidgetDiscountText: (originalAmount: number) =>
        t('discounts.new_users_discount_plan_widget_text', {
          original_amount: formatMoney(originalAmount, 'EUR'),
        }),
      helper: {
        title: t('discounts.new_year_discount.helper.title'),
        content: t('discounts.new_year_discount.helper.text'),
      },
    }),
    [t],
  );

  const yearlyFirstUseDiscount: DiscountType = useMemo(
    () => ({
      couponId: yearlyPromocode,
      discountValue: 0.85,
      discountType: 'percentage',
      isActive: true,
      discountText: t('discounts.new_users_discount_text', {
        percent: 85,
      }),
      planWidgetDiscountText: (originalAmount: number) =>
        t('discounts.new_users_discount_plan_widget_text', {
          original_amount: formatMoney(originalAmount, 'EUR'),
        }),
      helper: {
        title: t('discounts.new_year_discount.helper.title'),
        content: t('discounts.new_year_discount.helper.text'),
      },
    }),
    [t],
  );

  const yearlyStarterDiscount: DiscountType = useMemo(
    () => ({
      couponId: starterDiscountPromocode,
      discountValue: 100,
      discountType: 'fixed',
      discountText: t('discounts.starter_discount_text', {
        amount: formatMoney(100),
      }),
      isActive: !!startedLessThanAYearAgo,
      onClickToggleDiscount: () => {
        if (!user?.VATNumber || !user?.startDate) {
          globalModalsController.openModal({
            id: 'starter-discount-modal',
            options: {
              width: '2xl',
              name: 'starter-discount-modal',
              color: 'taxes',
            },
            render: ({ closeModal }) => (
              <StarterDiscountForm onClose={closeModal} />
            ),
          });
        }
      },
      helper: {
        title: t('discounts.starter_discount.helper.title'),
        content: t('discounts.starter_discount.helper.text'),
      },
    }),
    [t, user?.VATNumber, user?.startDate, startedLessThanAYearAgo],
  );

  const discount: DiscountsType | undefined = useMemo(() => {
    const isOnYearlyPromocode =
      userSubscription?.coupon?.promocode &&
      userSubscription.coupon.promocode === yearlyPromocode;

    if (!user || isOnYearlyPromocode) return undefined;

    const isOnMonthlyPromocode =
      userSubscription?.coupon?.promocode &&
      userSubscription.coupon.promocode === monthlyPromocode &&
      userSubscription.coupon.date &&
      dayjs().diff(dayjs(userSubscription.coupon.date), 'month') < 3;

    if (
      (isNewYearUser &&
        isFreeOrTrialWithoutPreviousSubscription(user) &&
        user?.country == AvailableCountries.germany) ||
      isOnMonthlyPromocode
    ) {
      return {
        monthly: monthlyFirstUseDiscount,
        yearly: yearlyFirstUseDiscount,
      };
    }
    if (
      user?.country === AvailableCountries.belgium &&
      isFreeOrTrialWithoutPreviousSubscription(user) &&
      (!user?.VATNumber || !user?.startDate || startedLessThanAYearAgo)
    ) {
      return {
        monthly: undefined,
        yearly: yearlyStarterDiscount,
      };
    }
  }, [
    user,
    userSubscription?.coupon,
    monthlyFirstUseDiscount,
    yearlyFirstUseDiscount,
    isNewYearUser,
    yearlyStarterDiscount,
    startedLessThanAYearAgo,
  ]);

  return discount;
};

export default useDiscount;
